import React, { useState } from 'react';
import { CheckCircle, Users, Building2, Heart } from 'lucide-react';

const Project = () => {
  const [donationPacks, setDonationPacks] = useState(1);
  const breakfastsProvided = donationPacks * 25;
  const costPerPack = 119;
  const costPerBreakfast = 4.76;

  return (
    <div className="pt-20">
      {/* Hero Section with Live Impact Tracker */}
      <section className="bg-[#2C2416] text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#3D3426] rounded-full text-sm">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Live Impact Tracker
            </span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-bold mb-4 text-[#F5F1E8]">
            247,832
          </h1>
          <p className="text-3xl mb-8 text-[#E5DCC8]">
            breakfasts served
          </p>
          
          <p className="text-xl text-gray-300 mb-12">
            On our way to <span className="font-bold text-white">1,000,000</span> nutritious breakfasts for children<br />
            who need it most.
          </p>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-400">Progress to Goal</span>
              <span className="text-sm font-semibold text-[#C9A961]">24.8%</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-3">
              <div className="bg-gradient-to-r from-[#8B6F47] to-[#C9A961] h-3 rounded-full" style={{width: '24.8%'}}></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
              <span>0</span>
              <span>250K</span>
              <span>500K</span>
              <span>750K</span>
              <span>1M</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
            <a
              href="#donate"
              className="px-8 py-4 bg-[#8B6F47] text-white rounded-full hover:bg-[#C9A961] transition-colors font-medium text-lg"
            >
              Donate a Breakfast
            </a>
            <a
              href="#partner"
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full hover:bg-white hover:text-[#2C2416] transition-colors font-medium text-lg"
            >
              Become a Partner
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2C2416] mb-2">247,832</div>
              <div className="text-[#8B6F47] font-semibold mb-1">Breakfasts Served</div>
              <div className="text-sm text-gray-500">and counting</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2C2416] mb-2">156</div>
              <div className="text-[#8B6F47] font-semibold mb-1">Schools Reached</div>
              <div className="text-sm text-gray-500">across South Africa</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2C2416] mb-2">26</div>
              <div className="text-[#8B6F47] font-semibold mb-1">Essential Nutrients</div>
              <div className="text-sm text-gray-500">in every serving</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#2C2416] mb-2">100%</div>
              <div className="text-[#8B6F47] font-semibold mb-1">Same Formula</div>
              <div className="text-sm text-gray-500">no compromise</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-[#8B6F47] text-sm tracking-widest uppercase mb-4">
            OUR MISSION
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-8">
            Good nutrition should lift everybody.
          </h2>
          <p className="text-xl text-[#5C4033] mb-4 leading-relaxed">
            Some wake up with choices. Others wake up hungry. GUAG/Project exists to close that gap. 
            GUAG delivers real nutrition — not empty calories — to school children who need it most.{' '}
            <span className="font-bold text-[#2C2416]">
              The same product. The same formulation. The same standard.
            </span>
          </p>
        </div>
      </section>

      {/* Road to One Million Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#8B6F47] text-sm tracking-widest uppercase mb-4">
              The Journey
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416]">
              Road to One Million
            </h2>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-[#E5DCC8]"></div>

            {/* Milestone 1 - Achieved */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="md:text-right">
                <div className="bg-[#F5F1E8] rounded-2xl p-6 inline-block">
                  <div className="text-3xl font-bold text-[#2C2416] mb-2">100K</div>
                  <div className="text-lg text-[#5C4033] mb-1">First 100,000 breakfasts</div>
                  <div className="text-sm text-[#8B6F47] mb-3">March 2025</div>
                  <div className="flex items-center gap-2 text-green-600 justify-end">
                    <CheckCircle size={16} />
                    <span className="text-sm font-semibold">Achieved</span>
                  </div>
                </div>
              </div>
              <div className="relative flex items-center">
                <div className="absolute left-0 md:left-auto md:-ml-4 w-4 h-4 bg-[#8B6F47] rounded-full border-4 border-white z-10"></div>
              </div>
            </div>

            {/* Milestone 2 - In Progress */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="relative flex items-center md:justify-end">
                <div className="absolute right-0 md:right-auto md:-mr-4 w-4 h-4 bg-gray-300 rounded-full border-4 border-white z-10"></div>
              </div>
              <div>
                <div className="bg-[#8B6F47] text-white rounded-2xl p-6 inline-block">
                  <div className="text-3xl font-bold mb-2">250K</div>
                  <div className="text-lg mb-1">Quarter million milestone</div>
                  <div className="text-sm text-[#E5DCC8] mb-3">Q2 2026</div>
                  <div className="inline-block px-3 py-1 bg-[#C9A961] rounded-full text-xs font-semibold">
                    In Progress
                  </div>
                </div>
              </div>
            </div>

            {/* Milestone 3 */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="md:text-right">
                <div className="bg-[#F5F1E8] rounded-2xl p-6 inline-block">
                  <div className="text-3xl font-bold text-[#2C2416] mb-2">500K</div>
                  <div className="text-lg text-[#5C4033] mb-1">Halfway to our goal</div>
                  <div className="text-sm text-[#8B6F47]">Q4 2026</div>
                </div>
              </div>
              <div className="relative flex items-center">
                <div className="absolute left-0 md:left-auto md:-ml-4 w-4 h-4 bg-gray-300 rounded-full border-4 border-white z-10"></div>
              </div>
            </div>

            {/* Milestone 4 */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="relative flex items-center md:justify-end">
                <div className="absolute right-0 md:right-auto md:-mr-4 w-4 h-4 bg-gray-300 rounded-full border-4 border-white z-10"></div>
              </div>
              <div>
                <div className="bg-[#F5F1E8] rounded-2xl p-6 inline-block">
                  <div className="text-3xl font-bold text-[#2C2416] mb-2">1M</div>
                  <div className="text-lg text-[#5C4033] mb-1">One million breakfasts</div>
                  <div className="text-sm text-[#8B6F47]">2027</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-[#8B6F47] text-sm tracking-widest uppercase mb-4">
              THE MODEL
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-4">
              How GUAG/PROJECT Works
            </h2>
            <p className="text-xl text-[#5C4033]">
              Three pathways to impact. One shared mission.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Method 1 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#8B6F47] rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xl font-bold">1</span>
                </div>
                <Users className="text-gray-300" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-[#2C2416] mb-3">
                Customer-Driven
              </h3>
              <p className="text-[#5C4033] mb-4">
                Buy a GUAG/PROJECT pack. We donate it on your behalf.
              </p>
              <div className="bg-[#F5F1E8] rounded-lg p-3">
                <p className="text-sm font-semibold text-[#5C4033]">
                  One purchase = one breakfast delivered.
                </p>
              </div>
            </div>

            {/* Method 2 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#8B6F47] rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xl font-bold">2</span>
                </div>
                <Building2 className="text-gray-300" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-[#2C2416] mb-3">
                Corporate-Funded
              </h3>
              <p className="text-[#5C4033] mb-4">
                National companies purchase GUAG in volume. These units are redirected into school feeding programmes.
              </p>
              <div className="bg-[#F5F1E8] rounded-lg p-3">
                <p className="text-sm font-semibold text-[#5C4033]">
                  Nutrition with accountability — not marketing noise.
                </p>
              </div>
            </div>

            {/* Method 3 */}
            <div className="bg-white rounded-2xl p-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#8B6F47] rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xl font-bold">3</span>
                </div>
                <Heart className="text-gray-300" size={32} />
              </div>
              <h3 className="text-2xl font-bold text-[#2C2416] mb-3">
                Our Commitment
              </h3>
              <p className="text-[#5C4033] mb-4">
                GUAG contributes directly. This responsibility is built into the business model.
              </p>
              <div className="bg-[#F5F1E8] rounded-lg p-3">
                <p className="text-sm font-semibold text-[#5C4033]">
                  This is not a campaign. It's structural.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Why Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#8B6F47] text-sm tracking-widest uppercase mb-4">
                THE WHY
              </p>
              <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-8 leading-tight">
                Growing bodies and minds require more than energy.
              </h2>
              
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-1" size={20} />
                  <span className="text-[#5C4033]">Protein for growth and repair</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-1" size={20} />
                  <span className="text-[#5C4033]">Zinc for immune resilience and development</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-1" size={20} />
                  <span className="text-[#5C4033]">Vitamins and minerals for learning and recovery</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-1" size={20} />
                  <span className="text-[#5C4033]">Immune support from Sumodulin™</span>
                </li>
              </ul>

              <blockquote className="border-l-4 border-[#8B6F47] pl-6 italic text-xl text-[#5C4033]">
                "Deficiency doesn't announce itself. It quietly limits potential."
              </blockquote>
            </div>

            <div>
              <img
                src="https://d64gsuwffb70l.cloudfront.net/6851587676ae218610324179_1769273874523_f7a49c81.png"
                alt="GUAG Products"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
              <div className="bg-[#F5F1E8] rounded-2xl p-6 mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-4xl font-bold text-[#2C2416]">25</div>
                  <span className="text-sm text-[#8B6F47]">servings per pack</span>
                </div>
                <h3 className="text-lg font-bold text-[#2C2416] mb-2">
                  GUAG™ Product Bulletin
                </h3>
                <p className="text-sm text-[#5C4033] mb-4">
                  Download our comprehensive 2-page product bulletin covering key nutritional highlights, 
                  bioavailable Zinc from Zinc Picolinate, protein, Omega-3, cognitive health benefits, 
                  shelf stability, and more.
                </p>
                <a
                  href="#"
                  className="inline-block px-6 py-3 bg-[#8B6F47] text-white rounded-full hover:bg-[#5C4033] transition-colors font-medium"
                >
                  Download PDF
                </a>
                <p className="text-xs text-gray-500 mt-3">
                  Confidential document — Azura Health Science Pty Ltd
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Take Action Section */}
      <section id="donate" className="py-20 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto px-6 text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C2416] mb-4">
            Take Action
          </h2>
          <p className="text-xl text-[#5C4033]">
            Every contribution brings us closer to our goal.
          </p>
        </div>

        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Donate a Pack */}
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-[#2C2416] mb-2">
                Donate a Pack
              </h3>
              <p className="text-[#8B6F47] mb-6">Individual impact</p>
              
              <p className="text-[#5C4033] mb-6">
                Each 1.25kg pack provides <span className="font-bold">25 nutritious breakfasts</span> to school children in need. 
                Same product, same quality, same impact.
              </p>

              {/* Donation Calculator */}
              <div className="bg-[#F5F1E8] rounded-xl p-6 mb-6">
                <label className="block text-sm font-semibold text-[#5C4033] mb-3">
                  Packs to donate:
                </label>
                <div className="flex items-center gap-4 mb-4">
                  <button
                    onClick={() => setDonationPacks(Math.max(1, donationPacks - 1))}
                    className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-2xl font-bold text-[#8B6F47] hover:bg-[#8B6F47] hover:text-white transition-colors"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={donationPacks}
                    onChange={(e) => setDonationPacks(Math.max(1, parseInt(e.target.value) || 1))}
                    className="flex-1 text-center text-2xl font-bold text-[#2C2416] bg-white rounded-lg px-4 py-2"
                  />
                  <button
                    onClick={() => setDonationPacks(donationPacks + 1)}
                    className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-2xl font-bold text-[#8B6F47] hover:bg-[#8B6F47] hover:text-white transition-colors"
                  >
                    +
                  </button>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5C4033]">Breakfasts provided:</span>
                    <span className="font-bold text-[#2C2416]">{breakfastsProvided}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-[#2C2416]">R{costPerPack * donationPacks}</span>
                    <span className="text-[#8B6F47]">R{costPerBreakfast.toFixed(2)} per breakfast</span>
                  </div>
                </div>
              </div>

              <button className="w-full px-8 py-4 bg-[#8B6F47] text-white rounded-full hover:bg-[#5C4033] transition-colors font-medium text-lg">
                Donate {donationPacks} Pack{donationPacks > 1 ? 's' : ''} Now
              </button>
            </div>

            {/* Partner with Us */}
            <div id="partner" className="bg-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-[#2C2416] mb-2">
                Partner with Us
              </h3>
              <p className="text-[#8B6F47] mb-6">Corporate impact</p>
              
              <p className="text-[#5C4033] mb-6">
                Corporate partnerships enable <span className="font-bold">large-scale impact</span>. 
                Volume purchases are redirected directly into school feeding programmes.
              </p>

              <div className="bg-[#F5F1E8] rounded-xl p-6 mb-6">
                <p className="font-semibold text-[#2C2416] mb-3">Partnership benefits:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-[#5C4033]">Measurable social impact</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-[#5C4033]">Transparent reporting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#8B6F47] flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-[#5C4033]">Brand alignment with purpose</span>
                  </li>
                </ul>
              </div>

              <button className="w-full px-8 py-4 bg-white border-2 border-[#8B6F47] text-[#8B6F47] rounded-full hover:bg-[#8B6F47] hover:text-white transition-colors font-medium text-lg">
                Enquire About Partnership
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#2C2416] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-[#C9A961] text-sm tracking-widest uppercase mb-4">
            Join the movement
          </p>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Together, we reach one million.
          </h2>
          <p className="text-xl text-gray-300 mb-12">
            Every breakfast counts. Every contribution matters. Every child deserves nutrition that supports their potential.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="#donate"
              className="px-8 py-4 bg-[#8B6F47] text-white rounded-full hover:bg-[#C9A961] transition-colors font-medium text-lg"
            >
              Donate a Pack
            </a>
            <a
              href="#partner"
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full hover:bg-white hover:text-[#2C2416] transition-colors font-medium text-lg"
            >
              Partner with GUAG/PROJECT
            </a>
            <a
              href="/"
              className="px-8 py-4 bg-transparent border-2 border-[#C9A961] text-[#C9A961] rounded-full hover:bg-[#C9A961] hover:text-white transition-colors font-medium text-lg"
            >
              Buy GUAG
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Project;